import { CHARACTER_GALLERIES, CHARACTER_MEDIA } from "./Collections";
import { CHARACTER_QUOTES } from "./quotes";

const getCharacterQuote = (slug, explicitQuote) =>
	explicitQuote || CHARACTER_QUOTES[slug] || CHARACTER_QUOTES[slug.replace(/-/g, "")];

const createCharacter = ({
	name,
	slug,
	category,
	epithet,
	quote,
	intro,
	traits,
	arc,
	videoQuery,
	analysisQuery,
	analysisLabel = "Character analysis",
	plotConnection,
}) => ({
	name,
	slug,
	category,
	epithet,
	image: CHARACTER_MEDIA[slug].image,
	cutout: CHARACTER_MEDIA[slug].cutout,
	intro,
	plotConnection,
	quote: getCharacterQuote(slug, quote),
	traits,
	arc,
	gallery: CHARACTER_GALLERIES[slug] || [],
	videos: [
		{
			label: `${name} lore videos`,
			url: `https://www.youtube.com/results?search_query=${encodeURIComponent(videoQuery)}`,
		},
		{
			label: analysisLabel,
			url: `https://www.youtube.com/results?search_query=${encodeURIComponent(analysisQuery)}`,
		},
	],
});

export const CHARACTERS = [
	createCharacter({
		name: "Guts",
		slug: "guts",
		category: "Strugglers",
		epithet: "The Black Swordsman",
		intro: [
			"Guts starts out looking almost inhuman: all iron, exhaustion, and spite. The longer you stay with him, the more you realize how much of Berserk is about what it costs a person to keep going after the world has already taken too much.",
			"He is at his most interesting when the story lets rage and tenderness sit side by side. That tension is really the whole character.",
			"By the time the manga opens up into a traveling-party story, Guts stops being just a weapon pointed at the past and becomes someone painfully trying to remain present for other people.",
		],
		plotConnection: [
			"Guts is the spine of Berserk. The story keeps changing shape around him: revenge tale, war chronicle, horror epic, road story, and something almost like a wounded fantasy of recovery.",
			"His choices push nearly every major turn. Leaving the Hawks cracks Griffith open, surviving the Eclipse gives the story its deepest scar, and choosing to protect Casca changes the whole direction of his journey.",
		],
		traits: ["Relentless", "Guarded", "Tender beneath armor", "Defiant"],
		arc: "From survival to vengeance to the terrifying work of protecting what remains.",
		videoQuery: "Berserk Guts lore",
		analysisQuery: "Berserk Black Swordsman analysis",
		analysisLabel: "Black Swordsman analysis",
	}),
	createCharacter({
		name: "Griffith",
		slug: "griffith",
		category: "Falconia",
		epithet: "The Falcon of Light",
		intro: [
			"Griffith walks into the story like someone from a different genre: too composed, too beautiful, too sure of himself. That first impression is part of the trap.",
			"Miura makes him compelling by letting the dream feel real. You can understand why people would follow him, admire him, and mistake his vision for grace.",
			"What makes him unforgettable is that Berserk never lets ambition stay abstract. It always has a body count.",
		],
		plotConnection: [
			"Griffith is the hinge Berserk swings on. The Golden Age rises because of him, the Eclipse happens because of him, and Falconia exists because the world still cannot stop bending around his dream.",
			"He is not just Guts' enemy. He is the reason the story hurts the way it does.",
		],
		traits: ["Magnetic", "Visionary", "Possessive", "Terrifyingly composed"],
		arc: "A dream so brilliant that it becomes indistinguishable from ruin.",
		videoQuery: "Berserk Griffith lore",
		analysisQuery: "Berserk Griffith character analysis",
		analysisLabel: "Falcon of Light analysis",
	}),
	createCharacter({
		name: "Casca",
		slug: "casca",
		category: "Strugglers",
		epithet: "Commander of the Hawks",
		intro: [
			"Casca has one of the hardest jobs in Berserk: she has to feel like a real soldier, a real friend, and a real person under pressure, not just the emotional center of two men.",
			"That is why she matters so much. She carries command, exhaustion, love, resentment, and survival all at once.",
			"When the story breaks her, it changes the emotional weather of the entire manga.",
		],
		plotConnection: [
			"Casca ties the Golden Age to everything that comes after it. Through her, Berserk stops being only about vengeance and becomes a story about care, guilt, trauma, and whether restoration is even possible.",
			"So much of Guts' later journey only makes sense because Casca is still there, still wounded, and still worth remaining human for.",
		],
		traits: ["Loyal", "Capable", "Proud", "Deeply wounded"],
		arc: "A commander, survivor, and emotional center whose recovery refuses to be simple.",
		videoQuery: "Berserk Casca lore",
		analysisQuery: "Berserk Casca character analysis",
		analysisLabel: "Casca character analysis",
	}),
	createCharacter({
		name: "Zodd",
		slug: "zodd",
		category: "Apostles",
		epithet: "Nosferatu Zodd",
		intro: [
			"Zodd feels like the moment Berserk stops pretending the world is merely cruel and admits that it is also monstrous.",
			"He has a warrior's clarity to him. He understands people through struggle, and that gives him a strange kind of brutal honesty.",
			"Every time he appears, the scale of the story gets bigger.",
		],
		plotConnection: [
			"Zodd is one of the first unmistakable signs that Griffith's path leads somewhere far beyond politics or war. He warns, confirms, escorts, and escalates.",
			"He also helps tie the human battlefield to the apostolic world that keeps swallowing it.",
		],
		traits: ["Battle-hungry", "Ancient", "Honorable in combat", "Monstrous"],
		arc: "The immortal warning sign that the battlefield is only the surface of the world.",
		videoQuery: "Berserk Zodd lore",
		analysisQuery: "Berserk Apostles explained",
		analysisLabel: "Apostles explained",
	}),
	createCharacter({
		name: "Skull Knight",
		slug: "skull-knight",
		category: "Mythic",
		epithet: "The Struggler's Omen",
		intro: [
			"Skull Knight has the presence of a warning that learned how to ride a horse and carry a sword.",
			"He arrives, says exactly enough to unsettle everyone, and leaves the page colder than he found it.",
		],
		plotConnection: [
			"Skull Knight is how Berserk keeps reminding you that the story is larger than what Guts can currently see. He links private grief to ancient cycles, relics, and the long war against the God Hand.",
			"Whenever he enters, the manga's horizon widens.",
		],
		traits: ["Cryptic", "Ancient", "Noble", "Ruthlessly focused"],
		arc: "A guide and warning whose help always carries the weight of something unfinished.",
		videoQuery: "Berserk Skull Knight lore",
		analysisQuery: "Berserk causality explained",
		analysisLabel: "Causality explained",
	}),
	createCharacter({
		name: "Farnese",
		slug: "farnese",
		category: "Holy",
		epithet: "From Zeal to Care",
		intro: [
			"Farnese starts off brittle, frightened, and severe in a way that can be difficult to like, which is part of why her growth lands so well.",
			"She is one of the manga's best examples of a person changing not because the story forgives her cheaply, but because it keeps forcing her to confront herself.",
		],
		plotConnection: [
			"Farnese matters because she changes the texture of the later story. She turns one corner of Berserk away from punishment and toward care, work, and gradual spiritual growth.",
			"Once she joins the party, the manga gains a new kind of emotional labor it did not have before.",
		],
		traits: ["Conflicted", "Devout", "Growing", "Protective"],
		arc: "A brittle certainty slowly reshaped into compassion and courage.",
		videoQuery: "Berserk Farnese lore",
		analysisQuery: "Berserk Conviction arc analysis",
		analysisLabel: "Conviction arc analysis",
	}),
	createCharacter({
		name: "Charlotte",
		slug: "charlotte",
		category: "Midland",
		epithet: "Princess of Midland",
		intro: [
			"Charlotte is easy to underestimate because she enters the story through courtly softness rather than force.",
			"But that softness is part of the machinery of the plot. Her longing, innocence, and position all become politically dangerous the moment Griffith steps near her.",
		],
		plotConnection: [
			"Charlotte is one of the human bridges between dream and disaster. Her relationship with Griffith helps trigger his imprisonment and, by extension, the catastrophe that follows.",
			"Later, she also becomes part of Falconia's image of grace and legitimacy.",
		],
		traits: ["Tender", "Idealistic", "Romantic", "Politically exposed"],
		arc: "A royal figure whose softness is constantly pressed into the machinery of other ambitions.",
		videoQuery: "Berserk Charlotte lore",
		analysisQuery: "Berserk Charlotte Griffith analysis",
		analysisLabel: "Golden Age relationship analysis",
	}),
	createCharacter({
		name: "Conrad",
		slug: "conrad",
		category: "God Hand",
		epithet: "Plague-Bearer of the God Hand",
		intro: [
			"Conrad feels like sickness given shape. He does not need much page time to leave a residue behind.",
			"That is what makes him memorable. He is not the loudest of the God Hand, but he may be the one who feels most like bad air settling over a city.",
		],
		plotConnection: [
			"Conrad helps the God Hand feel broader than a set of personalities. He is part of the atmosphere they bring with them: decay, dread, and the sense that suffering can spread beyond the moment that caused it.",
			"He matters because he deepens the feeling that the world's corruption is systemic, not local.",
		],
		traits: ["Unsettling", "Inhuman", "Silent", "Contagious"],
		arc: "A God Hand presence that makes corruption feel atmospheric and unstoppable.",
		videoQuery: "Berserk Conrad lore",
		analysisQuery: "Berserk God Hand Conrad analysis",
		analysisLabel: "God Hand analysis",
	}),
	createCharacter({
		name: "Femto",
		slug: "femto",
		category: "God Hand",
		epithet: "Fifth of the God Hand",
		intro: [
			"Femto is terrifying because he is not a stranger. He is Griffith after the story has burned away every limit that made his beauty readable as human.",
			"The result is not power in some simple fantasy sense. It is intimacy turned inside out and made cosmic.",
		],
		plotConnection: [
			"Femto is the form the Eclipse gives Griffith's betrayal. Once he exists, Berserk stops being only a tragedy of ambition and becomes a story haunted by the supernatural permanence of what was done.",
			"He is one of the reasons the manga never lets the past stay in the past.",
		],
		traits: ["Cold", "Transcendent", "Cruel", "Unreachable"],
		arc: "The point where personal betrayal becomes metaphysical horror.",
		videoQuery: "Berserk Femto lore",
		analysisQuery: "Berserk Femto character analysis",
	}),
	createCharacter({
		name: "Flora",
		slug: "flora",
		category: "Allies",
		epithet: "Witch of the Spirit Tree",
		intro: [
			"Flora has the kind of presence that immediately changes the temperature of the story. Around her, Berserk feels older, quieter, and more patient.",
			"She gives magical knowledge the weight of lived experience instead of exposition.",
		],
		plotConnection: [
			"Flora helps the series widen beyond iron and trauma into magic, memory, and spiritual inheritance. She is one of the people who makes the later journey possible at all.",
			"Without her, Guts' path toward the Berserker Armor and Schierke's deeper role would land very differently.",
		],
		traits: ["Wise", "Ancient", "Protective", "Composed"],
		arc: "A guardian figure whose knowledge expands the world beyond steel and revenge.",
		videoQuery: "Berserk Flora lore",
		analysisQuery: "Berserk Flora analysis",
	}),
	createCharacter({
		name: "Ganishka",
		slug: "ganishka",
		category: "Kushan",
		epithet: "Emperor of the Kushan",
		intro: [
			"Ganishka is what happens when imperial scale and apostolic scale stop being separate things. He is huge in every sense that matters: politically, physically, and catastrophically.",
			"He does not just threaten people. He distorts the shape of the world around them.",
		],
		plotConnection: [
			"Ganishka pushes Berserk outward into full-scale upheaval. Through him, Griffith's rise becomes a historical event, not just a personal one.",
			"He is also central to the collapse of the old boundary between the physical and astral worlds.",
		],
		traits: ["Imperial", "Monstrous", "Paranoid", "Cataclysmic"],
		arc: "An emperor whose power escalates until it begins to tear the world itself apart.",
		videoQuery: "Berserk Ganishka lore",
		analysisQuery: "Berserk Ganishka analysis",
	}),
	createCharacter({
		name: "Isidro",
		slug: "isidro",
		category: "Strugglers",
		epithet: "Aspiring Swordsman",
		intro: [
			"Isidro brings some badly needed nerve into Berserk, and not the noble kind. He is loud, eager, insecure, funny, and always trying to become a bigger version of himself.",
			"That makes him useful in a story where so many people already feel broken open by the past.",
		],
		plotConnection: [
			"Isidro helps the later party feel like a living group instead of a procession of damaged adults. Through him, Berserk gets to show apprenticeship, aspiration, and the awkwardness of growing up near legends.",
			"He gives Guts a different kind of witness than the story had before.",
		],
		traits: ["Restless", "Cocky", "Earnest", "Adaptable"],
		arc: "A would-be hero growing up beside monsters, myths, and impossible mentors.",
		videoQuery: "Berserk Isidro lore",
		analysisQuery: "Berserk Isidro analysis",
	}),
	createCharacter({
		name: "Isma",
		slug: "isma",
		category: "Allies",
		epithet: "Merrow Girl",
		intro: [
			"Isma is one of those later characters who reminds you Berserk still has room for wonder, not just dread.",
			"She feels playful and odd in a way that makes the world larger rather than lighter.",
		],
		plotConnection: [
			"Isma helps the sea journey and the later fantasy material feel inhabited instead of decorative. She is part of the story's attempt to keep beauty and strangeness alive next to all the ruin.",
			"That contrast matters more in Berserk than it would in a gentler series.",
		],
		traits: ["Curious", "Playful", "Otherworldly", "Warm"],
		arc: "A fleeting but vivid reminder that fantasy in Berserk is not only terror.",
		videoQuery: "Berserk Isma lore",
		analysisQuery: "Berserk Isma analysis",
	}),
	createCharacter({
		name: "Schierke",
		slug: "schierke",
		category: "Strugglers",
		epithet: "Witch of the Traveling Party",
		intro: [
			"Schierke is one of the most important later additions because she changes what competence looks like in Berserk. She does not dominate through force. She holds things together.",
			"Her calm is never empty. It comes from training, discipline, and the fact that she sees more of the world's invisible pressure than most people do.",
		],
		plotConnection: [
			"Schierke is how the manga becomes legible on the magical and astral level without losing emotional stakes. She translates, protects, and intervenes.",
			"She is also essential to the question of whether Guts can use monstrous power without disappearing into it.",
		],
		traits: ["Wise", "Composed", "Protective", "Spiritually alert"],
		arc: "A young witch who turns knowledge into care, restraint, and survival.",
		videoQuery: "Berserk Schierke lore",
		analysisQuery: "Berserk Schierke analysis",
	}),
	createCharacter({
		name: "Moonlight Boy",
		slug: "moonlightboy",
		category: "Mythic",
		epithet: "Child of the Full Moon",
		intro: [
			"Moonlight Boy arrives like relief and unease at the same time. Every appearance feels gentle until you remember the story he belongs to.",
			"He is one of Miura's cruelest and kindest ideas in the same body.",
		],
		plotConnection: [
			"Moonlight Boy links some of Berserk's deepest wounds to its rarest moments of tenderness. Through him, the series folds parenthood, grief, embodiment, and impossible return into one figure.",
			"He is also one of the clearest signs that the story's metaphysics are inseparable from its emotional damage.",
		],
		traits: ["Tender", "Mysterious", "Melancholic", "Uncanny"],
		arc: "A miraculous presence that binds love, grief, and horror into one figure.",
		videoQuery: "Berserk Moonlight Boy lore",
		analysisQuery: "Berserk Moonlight Boy analysis",
	}),
	createCharacter({
		name: "Mozgus",
		slug: "mozgus",
		category: "Holy",
		epithet: "The Iron Zealot",
		intro: [
			"Mozgus does not feel like a man who found religion. He feels like religion calcified into a body and started speaking in orders.",
			"He is frightening because his certainty never flickers.",
		],
		plotConnection: [
			"Mozgus is central to the Conviction arc's argument that human institutions can become monstrous without any help from apostles. He turns piety into spectacle and punishment into ritual.",
			"Through him, Berserk makes spiritual terror feel horribly earthly.",
		],
		traits: ["Fanatical", "Punishing", "Rigid", "Terrifyingly certain"],
		arc: "A holy enforcer whose certainty becomes one of the story's cruelest spectacles.",
		videoQuery: "Berserk Mozgus lore",
		analysisQuery: "Berserk Mozgus analysis",
	}),
	createCharacter({
		name: "Serpico",
		slug: "serpico",
		category: "Holy",
		epithet: "The Hidden Blade",
		intro: [
			"Serpico is one of those characters who can make stillness feel dangerous. He notices everything, speaks lightly, and rarely wastes motion.",
			"He survives by reading people fast and by never pretending the world is kinder than it is.",
		],
		plotConnection: [
			"Serpico helps anchor Farnese's story and gives the traveling party a different kind of intelligence than Guts or Schierke bring. He is tactical, skeptical, and emotionally difficult in useful ways.",
			"His presence keeps the party from ever becoming too simple or too harmonious.",
		],
		traits: ["Elegant", "Pragmatic", "Protective", "Deadly"],
		arc: "A swordsman whose grace and caution become a different answer to violence.",
		videoQuery: "Berserk Serpico lore",
		analysisQuery: "Berserk Serpico character analysis",
	}),
	createCharacter({
		name: "Judeau",
		slug: "judeau",
		category: "Hawks",
		epithet: "The Hawk Who Understood Everyone",
		intro: [
			"Judeau is the kind of person a group only fully understands after losing him. He notices quietly, gives more than he asks for, and never turns his own sensitivity into theater.",
			"That decency makes him hit harder in memory than he first does on the page.",
		],
		plotConnection: [
			"Judeau helps the Golden Age feel inhabited by real people rather than just icons on their way to tragedy. He gives the Hawks tact, warmth, and emotional intelligence.",
			"When Berserk wants the loss of that group to feel personal, Judeau is one of the names doing the work.",
		],
		traits: ["Perceptive", "Loyal", "Selfless", "Grounded"],
		arc: "A quiet center of the Hawks whose decency becomes sharper in retrospect.",
		videoQuery: "Berserk Judeau lore",
		analysisQuery: "Berserk Band of the Hawk analysis Judeau",
		analysisLabel: "Band of the Hawk analysis",
	}),
	createCharacter({
		name: "Pippin",
		slug: "pippin",
		category: "Hawks",
		epithet: "The Gentle Wall",
		intro: [
			"Pippin has very little interest in talking himself up, which makes his steadiness even more effective.",
			"He feels protective in a way that is physical first. You understand him through presence before you understand him through dialogue.",
		],
		plotConnection: [
			"Pippin is part of what makes the Hawks feel safe before Berserk destroys that safety. He helps the Golden Age build trust at the level of pure presence.",
			"That is a quiet role, but it matters enormously to the arc's emotional foundation.",
		],
		traits: ["Strong", "Quiet", "Dependable", "Warm-hearted"],
		arc: "A massive protector whose silence never reads as emptiness.",
		videoQuery: "Berserk Pippin lore",
		analysisQuery: "Berserk Pippin analysis",
	}),
	createCharacter({
		name: "Puck",
		slug: "puck",
		category: "Strugglers",
		epithet: "Elf Companion",
		intro: [
			"Puck keeps Berserk from sealing itself shut. He jokes, nags, reacts, and keeps a little airflow in places where the story would otherwise become suffocating.",
			"That does not make him lightweight. It makes him necessary.",
		],
		plotConnection: [
			"Puck is one of the earliest characters who forces Guts back into relation with another living being. He is witness, conscience, irritation, and comfort all at once.",
			"He helps the Black Swordsman material become readable as pain, not just aggression.",
		],
		traits: ["Playful", "Compassionate", "Curious", "Loyal"],
		arc: "An elf who keeps despair from sealing the story shut.",
		videoQuery: "Berserk Puck lore",
		analysisQuery: "Berserk Puck character analysis",
	}),
	createCharacter({
		name: "Rickert",
		slug: "rickert",
		category: "Hawks",
		epithet: "Witness to What Remains",
		intro: [
			"Rickert's survival feels almost unfair at first, which is part of what makes it interesting. He has to live with the fact that he was not there, and then live long enough for that fact to become a life.",
			"He grows into one of Berserk's clearest moral voices without ever losing his grief.",
		],
		plotConnection: [
			"Rickert lets Berserk ask what surviving outside the center of the tragedy looks like. He becomes a carrier of memory, craft, and judgment rather than pure vengeance.",
			"His relationship to Griffith later on is one of the story's sharpest moral counters.",
		],
		traits: ["Earnest", "Brave", "Observant", "Principled"],
		arc: "A surviving Hawk whose moral clarity hardens with age.",
		videoQuery: "Berserk Rickert lore",
		analysisQuery: "Berserk Rickert analysis",
	}),
	createCharacter({
		name: "Roderick",
		slug: "roderick",
		category: "Allies",
		epithet: "Prince and Seafarer",
		intro: [
			"Roderick has the rare distinction of entering Berserk as a prince and not immediately making you brace for the worst.",
			"He feels generous without being soft, and useful without needing to dominate the page.",
		],
		plotConnection: [
			"Roderick helps the later journey work logistically and emotionally. He gives the party mobility, steadiness, and a different model of nobility than Griffith or Midland usually provide.",
			"That contrast matters more than it first appears to.",
		],
		traits: ["Capable", "Good-humored", "Steady", "Noble"],
		arc: "A rare princely figure whose usefulness is matched by decency.",
		videoQuery: "Berserk Roderick lore",
		analysisQuery: "Berserk Roderick analysis",
	}),
	createCharacter({
		name: "Slan",
		slug: "slan",
		category: "God Hand",
		epithet: "Priestess of Ecstasy",
		intro: [
			"Slan is all appetite and performance. She does not merely appear; she luxuriates in appearing.",
			"That theatricality is part of what makes her so disturbing. She enjoys the collapse of boundaries other characters still need in order to remain human.",
		],
		plotConnection: [
			"Slan helps make the God Hand feel varied in texture, not just unified in power. Through her, Berserk pushes erotic horror, body horror, and spiritual desecration into the same space.",
			"She makes the series' cosmology feel intimate in the worst way.",
		],
		traits: ["Seductive", "Cruel", "Exultant", "Transgressive"],
		arc: "A God Hand member who turns appetite into spectacle and threat.",
		videoQuery: "Berserk Slan lore",
		analysisQuery: "Berserk Slan analysis",
	}),
	createCharacter({
		name: "Ubik",
		slug: "ubik",
		category: "God Hand",
		epithet: "Manipulator of Memory",
		intro: [
			"Ubik is grotesque in a way that first invites laughter and then poisons it. He plays with pain the way a trickster plays with cards.",
			"He is unnerving because he understands how memory can be bent until it starts cooperating with horror.",
		],
		plotConnection: [
			"Ubik is one of the reasons the Eclipse and the God Hand sequences feel psychologically invasive rather than merely spectacular. He helps turn recollection, shame, and self-knowledge into instruments of collapse.",
			"He makes inner vulnerability part of the battlefield.",
		],
		traits: ["Mocking", "Psychological", "Grotesque", "Cunning"],
		arc: "A God Hand figure who attacks the mind as eagerly as the body.",
		videoQuery: "Berserk Ubik lore",
		analysisQuery: "Berserk Ubik analysis",
	}),
	createCharacter({
		name: "Corkus",
		slug: "corkus",
		category: "Hawks",
		epithet: "The Bitter Hawk",
		intro: [
			"Corkus complains, postures, resents, and keeps showing up anyway. That is a big part of why he works.",
			"He makes the Hawks feel less idealized. There is ego in the group, pettiness in the group, and fear in the group, and Corkus carries a lot of that texture.",
		],
		plotConnection: [
			"Corkus helps the Golden Age avoid becoming too clean. He is one of the reminders that the Hawks are a real mercenary band, not just a myth in progress.",
			"When the story takes them away, it matters that it also takes away people this ordinary.",
		],
		traits: ["Cynical", "Jealous", "Blunt", "Humanly insecure"],
		arc: "A reminder that not every Hawk was noble, and that makes the loss feel fuller.",
		videoQuery: "Berserk Corkus lore",
		analysisQuery: "Berserk Corkus analysis",
	}),
	createCharacter({
		name: "Godot",
		slug: "godot",
		category: "Allies",
		epithet: "Blacksmith in Exile",
		intro: [
			"Godot is one of those rare Berserk characters who can say something simple and have it land harder than a speech.",
			"His value is not just that he offers shelter. It is that he offers judgment without performance.",
		],
		plotConnection: [
			"Godot is part of the story's attempt to build shelter in the aftermath of the Eclipse. He gives Guts a place, a weapon, and a perspective harsher than comfort but gentler than obsession.",
			"He helps keep the manga tied to work, craft, and ordinary human endurance.",
		],
		traits: ["Blunt", "Wise", "Protective", "World-weary"],
		arc: "A smith whose shelter and criticism both help temper Guts.",
		videoQuery: "Berserk Godot lore",
		analysisQuery: "Berserk Godot analysis",
	}),
	createCharacter({
		name: "Horse",
		slug: "horse",
		category: "Midland",
		epithet: "A reminder that Kentaro was crazy",
		intro: [
			"Horse is here less as an individual than as a reckoning that disgusting acts exist solely for the pleasure of demonic personalities.",
			"Even animals in this manga get folded into human violence so completely that they start getting pages of their own.",
		],
		plotConnection: [
			"The horse is a one-time character that was literally meant to traumatize people. It serves no other purpose, than to damage people's eyesights and imagination.",
			"It is one of those small visual threads that sticks with you years after reading Berserk.",
		],
		traits: ["Crazy", "Demonic", "Traumatizing", "Unsettling"],
		arc: "An isolated presence that helps make Berserk's imagery feel physical and cruel.",
		videoQuery: "Berserk horse scene analysis",
		analysisQuery: "Berserk war imagery analysis",	
		analysisLabel: "War imagery analysis",
	}),
	createCharacter({
		name: "Vargas",
		slug: "vargas",
		category: "Victims",
		epithet: "Broken Witness of the Black Swordsman Arc",
		intro: [
			"Vargas feels like the aftermath of horror given just enough time to speak. He is damaged, angry, frightened, and still trying to turn ruin into evidence.",
			"That rawness makes him memorable even with limited page time.",
		],
		plotConnection: [
			"Vargas helps define the early Black Swordsman material by showing what apostolic cruelty leaves behind in ordinary bodies. He is not mythic. He is the casualty report.",
			"Through him, Berserk tells you very early that revenge stories always have a civilian shadow.",
		],
		traits: ["Traumatized", "Determined", "Fearful", "Consumed by vengeance"],
		arc: "An early victim whose ruin teaches the scale of the horror ahead.",
		videoQuery: "Berserk Vargas lore",
		analysisQuery: "Berserk Black Swordsman arc analysis Vargas",
		analysisLabel: "Black Swordsman arc analysis",
	}),
	createCharacter({
		name: "Void",
		slug: "void",
		category: "God Hand",
		epithet: "Speaker of Causality",
		intro: [
			"Void has the stillness of something that has outlived any need to persuade. He does not feel charismatic. He feels structural.",
			"That makes him one of the most unsettling figures in the series, because he sounds less like a person making choices and more like a system explaining itself.",
		],
		plotConnection: [
			"Void is one of Berserk's clearest embodiments of causality as an idea. He helps define the God Hand not merely as monsters but as interpreters of a terrible order.",
			"When he speaks, the series shifts from horror to something almost liturgical.",
		],
		traits: ["Authoritative", "Ancient", "Calm", "Terrifyingly abstract"],
		arc: "The God Hand's gravest voice and one of the series' deepest embodiments of causality.",
		videoQuery: "Berserk Void lore",
		analysisQuery: "Berserk Void analysis",
	}),
];
